import React, { Component } from "react";
import { GET, POST, PUT, DELETE, Modal, DatePicker, alert, loading, t } from "./CustomComponent.js";
import { can } from "../utils/permission.js";
import { getCurrentCompany } from "../utils/localStorage";
import { formatNumber } from "../utils/formatting";

/**
 * เอกสารเบิกอะไหล่ (WBS) หลายใบต่อ 1 WO — การ์ด 6aea0538 (WO โครงการ BU05 เท่านั้น)
 * - default export WoWbsIssuePanel = การ์ดฝั่งขวา + modal สร้าง/แก้ร่าง + modal ดู + พิมพ์ A4 (ต้นแบบ: WoPickingListPanel)
 * - named export WoWbsPartsBoxes = กล่องแยกตาม Mat Doc ในแท็บ "อะไหล่" ของ Visit (ช่องใช้จริง PUT /:uid/used)
 * ปุ่มในการ์ดเป็น span role="button" เพราะ panel ถูกครอบด้วย <fieldset disabled> ของ modal WO (โหมดดูอย่างเดียว)
 * ที่ปิด <button> ทุกตัว แต่ผู้ใช้ยังต้องดู/พิมพ์เอกสารเดิมได้ — สองส่วนซิงก์กันผ่าน window event EVT_CHANGED
 */

const BASE = "admin/wo-wbs-issues";
const REASON_OTHER = "other";
const EVT_CHANGED = "wo-wbs-issue-changed";
const STATUS_ORDER = { draft: 0, fail: 1, send: 2, ok: 3, cxl: 4 };
const STATUS_CLASS = { draft: "bg-secondary-subtle text-secondary border", send: "bg-warning-subtle text-warning-emphasis border border-warning", ok: "bg-success-subtle text-success border border-success", fail: "bg-danger-subtle text-danger border border-danger", cxl: "bg-light text-secondary border" };
const POLL_MS = 15000;

const todayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${("0" + (d.getMonth() + 1)).slice(-2)}-${("0" + d.getDate()).slice(-2)}`;
};
const fmtD = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join("/") : "-");
const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]);
const broadcast = () => window.dispatchEvent(new Event(EVT_CHANGED));
const docNoList = (list) => (list || []).map((x) => (typeof x === "string" ? x : x?.doc_no || x?.no || "")).filter(Boolean);
const toList = (v) => (Array.isArray(v) ? v : v ? [v] : []);

const Act = ({ onClick, disabled, className = "", title, style, children }) => (
    <span
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled ? "true" : undefined}
        title={title}
        className={`${className} ${disabled ? "opacity-50" : "pointer"}`}
        style={{ cursor: disabled ? "not-allowed" : "pointer", userSelect: "none", ...style }}
        onClick={disabled ? undefined : onClick}
        onKeyDown={(e) => {
            if (!disabled && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                onClick && onClick(e);
            }
        }}
    >
        {children}
    </span>
);

const StatusChip = ({ doc }) => (
    <span className={`badge rounded-pill ${STATUS_CLASS[doc.status] || STATUS_CLASS.draft}`} style={doc.status === "cxl" ? { textDecoration: "line-through" } : undefined}>
        {doc.status === "send" && <span className="spinner-border spinner-border-sm me-1" style={{ width: 10, height: 10, borderWidth: 2 }} />}
        {t("wbi_status_" + doc.status)}
    </span>
);

const emptyEditor = (pl) => ({
    uid: pl?.uid || null,
    need_date: pl?.need_date ? String(pl.need_date).slice(0, 10) : "",
    reason: pl?.reason || "",
    remark: pl?.remark || "",
    items: (pl?.items || []).map((i) => ({
        part_uid: i.part_uid,
        part_number: i.part_number,
        part_name: i.part_name,
        uom_name: i.uom_name,
        wbs_element: i.wbs_element,
        stock_lot_uid: i.stock_lot_uid || "",
        quantity: i.qty ?? i.quantity ?? 1,
        info: null,
        ack: false,
    })),
    q: "",
    results: null,
    searching: false,
    banner: null,
    errors: {},
    tried: false,
    dirty: false,
    saving: false,
});

export default class WoWbsIssuePanel extends Component {
    state = { ctx: null, docs: [], ready: false, filter: "all", editor: null, viewing: null, confirm: null, shortInfo: null };
    searchTimer = null;
    searchSeq = 0;
    pollTimer = null;
    mounted = false;

    componentDidMount() {
        this.mounted = true;
        this.load();
        this.pollTimer = setInterval(() => {
            if (this.state.docs.some((d) => d.status === "send")) this.load(true);
        }, POLL_MS);
    }

    componentDidUpdate(prev) {
        if (prev.woUid !== this.props.woUid) this.load();
    }

    componentWillUnmount() {
        this.mounted = false;
        clearTimeout(this.searchTimer);
        clearInterval(this.pollTimer);
    }

    setSafe = (s) => this.mounted && this.setState(s);

    load = async (silent = false) => {
        const { woUid } = this.props;
        if (!woUid || !can("wo_wbs_issue.read")) return;
        const opt = { skipAccessDeniedAlert: true, ...(silent ? { silent: true } : {}) };
        const [ctx, docs] = await Promise.all([GET(`${BASE}/context/${woUid}`, null, true, opt), GET(BASE, { wo_uid: woUid }, true, opt)]);
        this.setSafe({ ctx: ctx?.status ? ctx.data : null, docs: docs?.status && Array.isArray(docs.data) ? docs.data : [], ready: true });
        if (silent) broadcast();
    };

    reload = async () => {
        await this.load();
        broadcast();
    };

    // ───────── helpers ─────────
    reasonLabel = (value) => (this.state.ctx?.reasons || []).find((r) => r.value === value)?.label || value || "-";
    isWoWbs = (w) => {
        const list = toList(this.state.ctx?.wo?.wo_wbs_elements).map((x) => (typeof x === "string" ? x : x?.wbs_element || x?.code));
        return list.includes(w);
    };
    vanLabel = () => {
        const v = this.state.ctx?.van;
        return v ? `${v.code || ""}${v.name ? " " + v.name : ""}`.trim() : "-";
    };

    // ───────── editor ─────────
    fetchParts = (search) => GET(`${BASE}/parts`, { wo_uid: this.props.woUid, search: search || "" });

    openEditor = async (doc) => {
        const editor = emptyEditor(doc);
        this.setState({ editor });
        if (editor.items.length) await this.refreshItemInfo(editor.items);
    };

    patchEditor = (patch, markDirty = true) => this.setState((s) => (s.editor ? { editor: { ...s.editor, ...patch, ...(markDirty ? { dirty: true } : {}) } } : null));

    // เปิดร่างเดิม: ดึงข้อมูลสต็อก/เตือนเบิกซ้ำของแต่ละอะไหล่ (ค้นด้วยรหัสแล้วจับคู่ part_uid)
    refreshItemInfo = async (itemsArg) => {
        const items = itemsArg || this.state.editor?.items || [];
        const found = await Promise.all(
            items.map(async (it) => {
                const r = await this.fetchParts(it.part_number);
                return (r?.status && Array.isArray(r.data) ? r.data : []).find((p) => p.part_uid === it.part_uid) || null;
            }),
        );
        this.setState((s) => (s.editor ? { editor: { ...s.editor, items: s.editor.items.map((it, ix) => ({ ...it, info: found[ix] || it.info })) } } : null));
    };

    onSearch = (q) => {
        this.patchEditor({ q, searching: true }, false);
        clearTimeout(this.searchTimer);
        const seq = ++this.searchSeq;
        this.searchTimer = setTimeout(async () => {
            if (!this.state.editor) return;
            const r = await this.fetchParts(q.trim());
            if (seq !== this.searchSeq || !this.mounted) return;
            this.patchEditor({ results: r?.status && Array.isArray(r.data) ? r.data : [], searching: false }, false);
        }, 350);
    };

    // WBS เริ่มต้น = WBS ของ WO ถ้ามีของ ไม่งั้นเลือก WBS แรกที่มีของ
    defaultWbs = (p) => {
        const withStock = (p.wbs || []).filter((w) => Number(w.available_qty) > 0);
        return (withStock.find((w) => this.isWoWbs(w.wbs_element)) || withStock[0] || {}).wbs_element || "";
    };
    defaultLot = (p, wbs) => {
        const lots = ((p.wbs || []).find((w) => w.wbs_element === wbs)?.lots || []).filter((l) => Number(l.available_qty) > 0);
        return lots[0]?.stock_lot_uid || "";
    };

    addPart = (p) => {
        const ed = this.state.editor;
        const wbs = this.defaultWbs(p);
        this.patchEditor({
            items: [...ed.items, { part_uid: p.part_uid, part_number: p.part_number, part_name: p.part_name, uom_name: p.uom_name, wbs_element: wbs, stock_lot_uid: this.defaultLot(p, wbs), quantity: 1, info: p, ack: false }],
            q: "",
            results: null,
            banner: null,
        });
    };

    setItem = (ix, patch) => this.patchEditor({ items: this.state.editor.items.map((it, i) => (i === ix ? { ...it, ...patch } : it)) });
    removeItem = (ix) => this.patchEditor({ items: this.state.editor.items.filter((_, i) => i !== ix) });
    onChangeWbs = (ix, wbs) => {
        const it = this.state.editor.items[ix];
        this.setItem(ix, { wbs_element: wbs, stock_lot_uid: it.info ? this.defaultLot(it.info, wbs) : "" });
    };

    lotOf = (it) => ((it.info?.wbs || []).find((w) => w.wbs_element === it.wbs_element)?.lots || []).find((l) => l.stock_lot_uid === it.stock_lot_uid) || null;
    dupInfo = (it) => {
        const inPlan = Number(it.info?.in_plan_qty || 0);
        const issued = docNoList(it.info?.issued_in_docs).filter((no) => no !== this.state.editor?.doc_no);
        return { inPlan, issued, any: inPlan > 0 || issued.length > 0 };
    };

    validate = (final) => {
        const ed = this.state.editor;
        const e = {};
        if (final) {
            if (!ed.need_date) e.need_date = t("wbi_err_need_date_required");
            else if (ed.need_date < todayStr()) e.need_date = t("wbi_err_need_date_past");
            if (!ed.reason) e.reason = t("wbi_err_reason_required");
            if (ed.reason === REASON_OTHER && !ed.remark.trim()) e.remark = t("wbi_err_remark_required");
            if (!ed.items.length) e.items = t("wbi_err_items_required");
        }
        const seen = {};
        ed.items.forEach((it, ix) => {
            const wbsRow = (it.info?.wbs || []).find((w) => w.wbs_element === it.wbs_element);
            const lot = this.lotOf(it);
            const q = Number(it.quantity);
            if (it.info) {
                if (!wbsRow || !(wbsRow.lots || []).some((l) => Number(l.available_qty) > 0)) e["w" + ix] = t("wbi_err_wbs_no_stock");
                else if (!it.stock_lot_uid) e["b" + ix] = t("wbi_err_lot_required");
            } else if (!it.stock_lot_uid) e["b" + ix] = t("wbi_err_lot_required");
            const key = `${it.part_uid}|${it.wbs_element}|${it.stock_lot_uid}`;
            if (it.stock_lot_uid && seen[key] !== undefined) e["b" + ix] = t("wbi_err_dup_line").replace("{n}", seen[key] + 1);
            seen[key] = ix;
            if (it.quantity === "" || !Number.isInteger(q) || q <= 0) e["q" + ix] = t("wbi_err_qty_invalid");
            else if (lot && q > Number(lot.available_qty)) e["q" + ix] = t("wbi_err_qty_exceeds").replace("{max}", formatNumber(lot.available_qty));
            if (final && this.dupInfo(it).any && !it.ack) e["a" + ix] = t("wbi_err_ack_dup");
        });
        return e;
    };

    buildPayload = (isNew) => {
        const ed = this.state.editor;
        return {
            ...(isNew ? { wo_uid: this.props.woUid } : {}),
            need_date: ed.need_date,
            reason: ed.reason,
            remark: ed.remark.trim(),
            items: ed.items.map((i) => ({ stock_lot_uid: i.stock_lot_uid, wbs_element: i.wbs_element, quantity: Number(i.quantity) })),
        };
    };

    closeEditor = () => {
        this.searchSeq++;
        clearTimeout(this.searchTimer);
        this.setState({ editor: null });
    };

    tryCloseEditor = () => {
        if (this.state.editor?.dirty) {
            this.setState({ confirm: { title: t("wbi_confirm_discard_title"), body: t("wbi_confirm_discard_body"), ok: t("wbi_discard"), cancel: t("wbi_back_to_edit"), danger: true, onOk: this.closeEditor } });
        } else this.closeEditor();
    };

    saveDraft = async () => {
        const ed = this.state.editor;
        const e = this.validate(false);
        if (!ed.items.length && !ed.need_date && !ed.reason && !ed.remark.trim()) {
            this.patchEditor({ banner: { kind: "warn", text: t("wbi_err_draft_empty") } }, false);
            return;
        }
        if (Object.keys(e).length) {
            this.patchEditor({ errors: e, tried: false, banner: { kind: "warn", text: t("wbi_err_fix_rows") } }, false);
            return;
        }
        this.patchEditor({ saving: true }, false);
        loading(true);
        const result = ed.uid ? await PUT(`${BASE}/${ed.uid}`, this.buildPayload(false)) : await POST(BASE, { ...this.buildPayload(true), submit: false });
        loading(false);
        this.patchEditor({ saving: false }, false);
        if (result?.status) {
            this.closeEditor();
            await this.reload();
            alert("", "success", t("success"), t("wbi_draft_saved"));
        }
    };

    askSend = () => {
        const ed = this.state.editor;
        const e = this.validate(true);
        this.patchEditor({ tried: true, banner: null }, false);
        if (Object.keys(e).length) return;
        const total = ed.items.reduce((a, i) => a + Number(i.quantity || 0), 0);
        const wbs = [...new Set(ed.items.map((i) => i.wbs_element))];
        this.setState({
            confirm: {
                title: t("wbi_confirm_send_title"),
                body: `${t("wbi_confirm_send_body").replace("{van}", this.vanLabel())}\n${formatNumber(ed.items.length)} ${t("wbi_items_unit")} · ${formatNumber(total)} ${t("wbi_qty_unit")} · WBS: ${wbs.join(", ")}\n${t("wbi_confirm_send_warn")}`,
                ok: t("wbi_send_sap"),
                onOk: this.saveAndSend,
            },
        });
    };

    // บันทึกเป็นร่างก่อน (ได้ uid) แล้วค่อยส่ง SAP — ถ้าส่งไม่ผ่าน ร่างยังอยู่ ไม่สร้างซ้ำเมื่อกดส่งใหม่
    saveAndSend = async () => {
        const ed = this.state.editor;
        if (!ed) return;
        this.patchEditor({ saving: true }, false);
        loading(true);
        let uid = ed.uid;
        let saved = uid ? await PUT(`${BASE}/${uid}`, this.buildPayload(false)) : await POST(BASE, { ...this.buildPayload(true), submit: false });
        if (saved?.status && !uid) {
            uid = saved.data?.uid;
            this.patchEditor({ uid }, false);
        }
        let result = null;
        if (saved?.status && uid) result = await POST(`${BASE}/${uid}/send`, {});
        loading(false);
        this.patchEditor({ saving: false }, false);
        if (!saved?.status || !uid) return; // api.js แจ้ง error ให้แล้ว
        if (result?.status) {
            this.closeEditor();
            await this.reload();
            this.notifySendResult(result.data);
            return;
        }
        // E4: สต็อกล็อตถูกเบิกตัดหน้า — แบนเนอร์ในหน้าต่าง + อัปเดตคงเหลือล่าสุด (ไม่ปิด modal)
        if (result?.errors?.code === "WBS_ISSUE_STOCK_CHANGED") {
            const changed = Array.isArray(result.errors.items) ? result.errors.items : [];
            this.patchEditor({ banner: { kind: "error", text: result.message || t("wbi_err_stock_changed"), items: changed }, tried: true }, false);
            await this.refreshItemInfo();
            return;
        }
        // error อื่นจาก SAP/ระบบ: api.js เด้ง alert แล้ว ร่างยังอยู่ — ปิดหน้าต่างให้ผู้ใช้ไปส่งซ้ำจากรายการ
        this.closeEditor();
        await this.reload();
    };

    notifySendResult = (doc) => {
        if (doc?.status === "ok") alert("", "success", t("success"), `${doc.doc_no || ""} · Mat Doc ${doc.mat_doc || ""}${doc.mat_year ? "/" + doc.mat_year : ""}`);
        else if (doc?.status === "fail") alert("", "warning", t("warning"), `${doc.doc_no || ""}: ${doc.sap_error || t("wbi_status_fail")}`);
        else alert("", "info", t("wbi_status_send"), `${doc?.doc_no || ""} ${t("wbi_waiting_sap")}`);
    };

    deleteDraft = (doc) =>
        this.setState({
            confirm: {
                title: t("wbi_confirm_delete_title"),
                body: t("wbi_confirm_delete_body"),
                ok: t("wbi_delete_draft"),
                danger: true,
                onOk: async () => {
                    loading(true);
                    const r = await DELETE(`${BASE}/${doc.uid}`);
                    loading(false);
                    if (r?.status) {
                        await this.reload();
                        alert("", "success", t("success"), t("wbi_draft_deleted"));
                    }
                },
            },
        });

    retryDoc = async (doc) => {
        loading(true);
        const r = await POST(`${BASE}/${doc.uid}/retry`, {});
        loading(false);
        if (r?.status) {
            await this.reload();
            this.notifySendResult(r.data);
            return;
        }
        if (r?.errors?.code === "WBS_ISSUE_RETRY_STOCK_SHORT") {
            this.setState({ shortInfo: { doc, message: r.message, items: Array.isArray(r.errors.items) ? r.errors.items : [] } });
            await this.reload();
        }
    };

    cancelDoc = (doc) =>
        this.setState({
            confirm: {
                title: t("wbi_confirm_cancel_title"),
                body: `${doc.doc_no}\n${t("wbi_confirm_cancel_body")}`,
                ok: t("wbi_cancel_doc"),
                cancel: t("cancel"),
                danger: true,
                onOk: async () => {
                    loading(true);
                    const r = await POST(`${BASE}/${doc.uid}/cancel`, {});
                    loading(false);
                    if (r?.status) {
                        await this.reload();
                        alert("", "success", t("success"), t("wbi_doc_cancelled"));
                    }
                },
            },
        });

    openView = async (doc) => {
        const r = await GET(`${BASE}/${doc.uid}`);
        if (r?.status) this.setState({ viewing: r.data });
    };

    // ───────── print A4 ─────────
    printDoc = async (doc) => {
        const r = await GET(`${BASE}/${doc.uid}/print`);
        if (!r?.status) return;
        const p = r.data || {};
        const co = getCurrentCompany();
        const rows = (p.items || [])
            .map((i, ix) => `<tr><td>${i.no ?? ix + 1}</td><td>${esc(i.part_number)}</td><td>${esc(i.part_name)}</td><td>${esc(i.wbs_element)}</td><td>${esc(i.lot)}</td><td class="r">${formatNumber(i.qty)}</td><td>${esc(i.uom_name)}</td></tr>`)
            .join("");
        const pad = Array.from({ length: Math.max(0, 8 - (p.items || []).length) }, () => `<tr><td colspan="7"></td></tr>`).join("");
        const meta = (k, v, span = 1) => `<div style="grid-column:span ${span}"><div class="k">${esc(k)}</div><div class="v">${esc(v)}</div></div>`;
        const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(p.doc_no)}</title><style>
@page{size:A4 portrait;margin:0}*{box-sizing:border-box}body{margin:0;font-family:Tahoma,Sarabun,sans-serif;font-size:13px;color:#000}
.a4{width:210mm;min-height:296mm;padding:14mm 14mm 10mm;display:flex;flex-direction:column}
.top{display:flex;align-items:center;border-bottom:2px solid #000;padding-bottom:10px}.top .co{font-size:16px;font-weight:700}.top .no{margin-left:auto;text-align:right;font-size:12px}.top .no b{font-size:18px;display:block}
.ttl{text-align:center;border-bottom:2px solid #000;padding:8px 0}.ttl b{font-size:20px;display:block}.ttl span{font-size:12px;letter-spacing:.5px}
.meta{display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px 18px;margin:14px 0}.k{font-size:11.5px;color:#333}.v{font-weight:700}
table{width:100%;border-collapse:collapse;border:1px solid #000}th{background:#eee;border-bottom:1px solid #000;padding:6px;text-align:left;font-size:12px}td{border-bottom:1px solid #999;padding:7px 6px;font-size:12px;height:32px}.r{text-align:right}
.signs{margin-top:auto;display:grid;grid-template-columns:repeat(3,1fr);gap:24px;text-align:center;padding-top:60px}.signs div{border-top:1px dotted #000;padding-top:6px;font-size:12px}
.foot{display:flex;justify-content:space-between;border-top:1px solid #999;margin-top:18px;padding-top:6px;font-size:11px;color:#333}
*{-webkit-print-color-adjust:exact;print-color-adjust:exact}</style></head><body><div class="a4">
<div class="top"><div class="co">${esc(p.company || co?.name || "")}</div><div class="no">${esc(t("wbi_doc_no"))}<b>${esc(p.doc_no)}</b>Mat Doc ${esc(p.mat_doc)}${p.mat_year ? "/" + esc(p.mat_year) : ""} · ${esc(p.movement_type)}</div></div>
<div class="ttl"><b>${esc(p.title || t("wbi_print_title"))}</b><span>PROJECT MATERIAL ISSUE</span></div>
<div class="meta">
${meta("WO", p.wo_number)}${meta(t("wbi_project"), p.project)}${meta(t("wbi_document_date"), p.document_date)}
${meta(t("wbi_requester"), p.requester_name)}${meta(t("wbi_route"), `Plant ${p.plant || ""} → VAN ${p.van || ""}`)}${meta(t("wbi_need_date"), fmtD(p.need_date))}
${meta(t("wbi_reason"), `${p.reason || ""}${p.remark ? " — " + p.remark : ""}`, 3)}
</div>
<table><thead><tr><th style="width:30px">#</th><th style="width:105px">${esc(t("wbi_part_number"))}</th><th>${esc(t("wbi_part_name"))}</th><th>WBS</th><th style="width:95px">${esc(t("wbi_lot"))}</th><th class="r" style="width:60px">${esc(t("wbi_qty"))}</th><th style="width:50px">${esc(t("wbi_uom"))}</th></tr></thead><tbody>${rows}${pad}</tbody></table>
<div class="signs">${[t("wbi_sign_requester"), t("wbi_sign_issuer"), t("wbi_sign_receiver")].map((s) => `<div><b>${esc(s)}</b><br>${esc(t("wbi_date"))} ____/____/______</div>`).join("")}</div>
<div class="foot"><span>${esc(t("wbi_printed_at"))} ${esc(new Date().toLocaleString("en-GB"))}</span><span>1/1</span></div>
</div><script>window.onload=function(){setTimeout(function(){window.print()},300)}</script></body></html>`;
        const w = window.open("", "_blank");
        if (!w) {
            alert("", "warning", t("warning"), t("wbi_popup_blocked"));
            return;
        }
        w.document.open();
        w.document.write(html);
        w.document.close();
    };

    // ───────── render ─────────
    renderCard() {
        const { ctx, docs, filter } = this.state;
        const canCreatePerm = can("wo_wbs_issue.create");
        const canUpdatePerm = can("wo_wbs_issue.update");
        const canDeletePerm = can("wo_wbs_issue.delete");
        const canSendPerm = can("wo_wbs_issue.send");
        const canCancelPerm = can("wo_wbs_issue.cancel");
        const createDisabled = !ctx.can_create || !canCreatePerm;
        const why = !ctx.can_create ? ctx.block_reason : !canCreatePerm ? t("access_denied_msg") : "";
        const cnt = (k) => docs.filter((d) => d.status === k).length;
        const list = [...docs].sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || String(b.doc_no || "z").localeCompare(String(a.doc_no || "z"))).filter((d) => filter === "all" || d.status === filter);
        const chip = (k, label, n) => (
            <Act key={k} className={`badge rounded-pill border ${filter === k ? "bg-dark text-white border-dark" : "bg-white text-dark"}`} onClick={() => this.setState({ filter: k })}>
                {label} {n}
            </Act>
        );

        return (
            <div className="rounded-3 p-3 mb-3 bg-white" style={{ border: "2px solid #2563EB" }}>
                <div className="d-flex align-items-start gap-2">
                    <div>
                        <div className="fw-bold text-16">{t("wbi_title")}</div>
                        <div className="text-12 text-secondary">
                            {t("wbi_subtitle")} · {formatNumber(docs.length)} {t("wbi_doc_unit")}
                        </div>
                    </div>
                    {canCreatePerm && (
                        <Act className="btn btn-primary btn-sm ms-auto text-nowrap" disabled={createDisabled} title={why} onClick={() => this.openEditor(null)}>
                            + {t("wbi_create")}
                        </Act>
                    )}
                </div>
                {why && canCreatePerm && <div className="alert alert-warning py-1 px-2 mt-2 mb-0 text-12">{why}</div>}
                <div className="bg-light rounded-2 px-2 py-1 mt-2 text-12">
                    {t("wbi_project")} <b>{ctx.project?.code || ctx.project?.project_code || ctx.project?.name || "-"}</b> → {t("wbi_van_label")} <b>{this.vanLabel()}</b> <span className="text-secondary">({t("wbi_van_note")})</span>
                </div>
                <div className="d-flex flex-wrap gap-1 mt-2">
                    {chip("all", t("all"), docs.length)}
                    {chip("draft", t("wbi_status_draft"), cnt("draft"))}
                    {chip("send", t("wbi_status_send"), cnt("send"))}
                    {chip("fail", t("wbi_status_fail_short"), cnt("fail"))}
                    {chip("ok", t("wbi_status_ok_short"), cnt("ok"))}
                    {cnt("cxl") > 0 && chip("cxl", t("wbi_status_cxl"), cnt("cxl"))}
                </div>

                {!docs.length ? (
                    <div className="border rounded-3 p-3 text-center text-secondary text-13 mt-3" style={{ borderStyle: "dashed" }}>
                        <b>{t("wbi_empty_title")}</b>
                        <br />
                        {t("wbi_empty_desc")}
                    </div>
                ) : !list.length ? (
                    <div className="border rounded-3 p-3 text-center text-secondary text-13 mt-3" style={{ borderStyle: "dashed" }}>
                        {t("wbi_empty_filter")}
                    </div>
                ) : (
                    <div className="mt-3">
                        {list.map((d) => {
                            const units = (d.items || []).reduce((a, i) => a + Number(i.qty ?? i.quantity ?? 0), 0);
                            const p = d.permissions || {};
                            const stale = d.status === "send" && d.is_stale;
                            const canRetry = (d.status === "fail" || stale) && p.can_retry;
                            return (
                                <div key={d.uid} className={`rounded-3 p-2 mb-2 ${d.status === "draft" ? "bg-light" : d.status === "fail" ? "" : "border"}`} style={d.status === "draft" ? { border: "1px dashed #9ca3af" } : d.status === "fail" ? { border: "1px solid #fca5a5", background: "#fff7f7" } : undefined}>
                                    <div className="d-flex align-items-start gap-2">
                                        <div className="flex-grow-1 min-w-0">
                                            <Act className={`fw-bold text-decoration-underline ${d.doc_no ? "text-primary" : "text-secondary"}`} onClick={() => (d.status === "draft" ? this.openEditor(d) : this.openView(d))} disabled={d.status === "draft" && !(canUpdatePerm && p.can_edit)}>
                                                {d.doc_no || t("wbi_no_number_yet")}
                                            </Act>
                                            <span className="ms-2">
                                                <StatusChip doc={d} />
                                            </span>
                                            <div className="text-12 text-secondary mt-1" style={{ lineHeight: 1.45 }}>
                                                {d.mat_doc ? `Mat Doc ${d.mat_doc}${d.mat_year ? "/" + d.mat_year : ""} · ${d.movement_type || ""}` : d.status === "send" ? t("wbi_waiting_mat_doc") : "—"}
                                                <br />
                                                {formatNumber((d.items || []).length)} {t("wbi_items_unit")} · {formatNumber(units)} {t("wbi_qty_unit")} · {String(d.sent_at || d.created_at || d.updated_at || "").slice(0, 16).replace("T", " ") || "-"} · {d.requester?.name || "-"}
                                            </div>
                                        </div>
                                        <div className="d-flex gap-1 flex-shrink-0">
                                            {d.status === "draft" && canUpdatePerm && (
                                                <Act className="btn btn-outline-secondary btn-sm" title={t("edit")} disabled={!p.can_edit} onClick={() => this.openEditor(d)}>
                                                    <span className="icon">{""}</span>
                                                </Act>
                                            )}
                                            {d.status === "draft" && canDeletePerm && (
                                                <Act className="btn btn-outline-danger btn-sm" title={t("delete")} disabled={!p.can_delete} onClick={() => this.deleteDraft(d)}>
                                                    <span className="icon">{""}</span>
                                                </Act>
                                            )}
                                            {(d.status === "fail" || stale) && canSendPerm && (
                                                <Act className="btn btn-outline-primary btn-sm" title={t("wbi_retry")} disabled={!canRetry} onClick={() => this.retryDoc(d)}>
                                                    <span className="icon">{""}</span>
                                                </Act>
                                            )}
                                            {d.status === "fail" && canCancelPerm && (
                                                <Act className="btn btn-outline-danger btn-sm" title={t("wbi_cancel_doc")} disabled={!p.can_cancel} onClick={() => this.cancelDoc(d)}>
                                                    <span className="icon">{""}</span>
                                                </Act>
                                            )}
                                            {d.status !== "draft" && (
                                                <Act className="btn btn-outline-secondary btn-sm" title={t("view")} onClick={() => this.openView(d)}>
                                                    <span className="icon">{""}</span>
                                                </Act>
                                            )}
                                            {d.status === "ok" && (
                                                <Act className="btn btn-outline-secondary btn-sm" title={t("print")} onClick={() => this.printDoc(d)}>
                                                    <span className="icon">{""}</span>
                                                </Act>
                                            )}
                                        </div>
                                    </div>
                                    {stale && <div className="text-12 mt-1" style={{ color: "#92400e" }}>{t("wbi_stale_hint").replace("{min}", ctx.stale_minutes || 5)}</div>}
                                    {d.status === "fail" && d.sap_error && <div className="text-12 mt-1" style={{ color: "#991b1b" }}>⚠ SAP: {d.sap_error}</div>}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }

    renderEditor() {
        const { ctx, editor: ed } = this.state;
        if (!ed) return null;
        const E = ed.tried ? this.validate(true) : this.validate(false);
        const total = ed.items.reduce((a, i) => a + (Number(i.quantity) || 0), 0);
        const err = (k) => E[k] && <div className="text-danger text-12 mt-1">{E[k]}</div>;
        return (
            <Modal show size="xl" onHide={this.tryCloseEditor} centered scrollable>
                <Modal.Header className="bg-white">
                    <Modal.Title className="fs-5">
                        {ed.uid ? t("wbi_edit_draft") : t("wbi_create")} ({t("wbi_from_wbs")}) <span className="badge bg-secondary ms-2">{t("wbi_status_draft")}</span>
                        <div className="text-12 text-secondary fw-normal">{t("wbi_no_number_hint")}</div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="row g-3">
                        <div className="col-md-3">
                            <label className="form-label fw-bold text-13">{t("wbi_ref_wo")}</label>
                            <input className="form-control bg-light" readOnly value={ctx.wo?.wo_number || ""} />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label fw-bold text-13">{t("wbi_project")}</label>
                            <input className="form-control bg-light" readOnly value={ctx.project?.code || ctx.project?.project_code || ""} title={ctx.project?.name || ""} />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label fw-bold text-13">{t("wbi_route")}</label>
                            <input className="form-control bg-light" readOnly value={`Project stock → VAN ${this.vanLabel()}`} />
                            <div className="text-12 text-secondary mt-1">{t("wbi_route_hint")}</div>
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-bold text-13">
                                {t("wbi_need_date")} <span className="text-danger">*</span>
                            </label>
                            <DatePicker type="th" className="form-control" value={ed.need_date} min={todayStr()} onChange={(v) => this.patchEditor({ need_date: v })} />
                            {err("need_date")}
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-bold text-13">
                                {t("wbi_reason")} <span className="text-danger">*</span>
                            </label>
                            <div className={`d-flex flex-column gap-1 ${E.reason ? "border border-danger rounded p-2" : ""}`}>
                                {(ctx.reasons || []).map((r) => (
                                    <label key={r.value} className="d-flex align-items-center gap-1 text-13" style={{ cursor: "pointer" }}>
                                        <input type="radio" name="wbi_reason" checked={ed.reason === r.value} onChange={() => this.patchEditor({ reason: r.value })} /> {r.label}
                                    </label>
                                ))}
                            </div>
                            {err("reason")}
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-bold text-13">
                                {t("wbi_remark")} {ed.reason === REASON_OTHER && <span className="text-danger">*</span>}
                            </label>
                            <textarea className={`form-control ${E.remark ? "is-invalid" : ""}`} rows={3} value={ed.remark} onChange={(e) => this.patchEditor({ remark: e.target.value })} />
                            {err("remark")}
                        </div>
                    </div>

                    <div className="mt-3 position-relative">
                        <label className="form-label fw-bold text-13">
                            {t("wbi_search_part")} {ctx.project?.code || ctx.project?.project_code || ""}
                        </label>
                        <input className="form-control" value={ed.q} placeholder={t("wbi_search_placeholder")} autoComplete="off" onChange={(e) => this.onSearch(e.target.value)} onFocus={() => ed.results === null && this.onSearch(ed.q)} />
                        {ed.results !== null && (
                            <div className="border rounded-2 mt-1 bg-white" style={{ maxHeight: 230, overflow: "auto" }}>
                                {ed.searching && <div className="p-2 text-secondary text-13">{t("loading")}</div>}
                                {!ed.searching && !ed.results.length && <div className="p-2 text-secondary text-13">{t("wbi_search_not_found")}</div>}
                                {ed.results.map((p) => {
                                    const added = ed.items.some((i) => i.part_uid === p.part_uid);
                                    const issued = docNoList(p.issued_in_docs);
                                    return (
                                        <div key={p.part_uid} className={`d-flex align-items-center gap-2 px-2 py-1 border-bottom text-13 ${p.out_of_stock ? "text-secondary bg-light" : ""}`}>
                                            <span className="fw-bold" style={{ width: 110 }}>{p.part_number}</span>
                                            <span className="flex-grow-1">
                                                {p.part_name}
                                                {Number(p.in_plan_qty) > 0 && <span className="badge bg-warning-subtle text-warning-emphasis ms-2">{t("wbi_tag_in_plan")}</span>}
                                                {issued.length > 0 && <span className="badge bg-warning-subtle text-warning-emphasis ms-2">{t("wbi_tag_issued")}</span>}
                                            </span>
                                            <span style={{ width: 210 }} className={`text-end ${p.out_of_stock ? "text-danger fw-bold" : "text-secondary"}`}>
                                                {p.out_of_stock ? t("wbi_out_of_stock_all") : `${t("wbi_available_total")} ${formatNumber(p.total_available_qty)} ${p.uom_name || ""} · ${formatNumber(p.wbs_with_stock)} WBS`}
                                            </span>
                                            <button type="button" className="btn btn-outline-primary btn-sm" disabled={p.out_of_stock || added} onClick={() => this.addPart(p)}>
                                                {p.out_of_stock ? t("wbi_cannot_select") : added ? t("wbi_already_added") : `+ ${t("wbi_add")}`}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="border rounded-3 mt-3 overflow-auto">
                        <table className="table table-sm mb-0 align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th className="text-center" style={{ width: 40 }}>#</th>
                                    <th>{t("wbi_part")}</th>
                                    <th style={{ minWidth: 230 }}>
                                        WBS <span className="text-danger">*</span>
                                    </th>
                                    <th style={{ minWidth: 190 }}>
                                        {t("wbi_lot")} <span className="text-danger">*</span>
                                    </th>
                                    <th className="text-end">{t("wbi_stock_left")}</th>
                                    <th className="text-end" style={{ width: 120 }}>
                                        {t("wbi_qty_issue")} <span className="text-danger">*</span>
                                    </th>
                                    <th style={{ width: 44 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {!ed.items.length ? (
                                    <tr>
                                        <td colSpan={7} className="text-center text-secondary py-3">{t("wbi_items_empty")}</td>
                                    </tr>
                                ) : (
                                    ed.items.map((it, ix) => {
                                        const lot = this.lotOf(it);
                                        const wbsRow = (it.info?.wbs || []).find((w) => w.wbs_element === it.wbs_element);
                                        const lots = wbsRow?.lots || [];
                                        const dup = this.dupInfo(it);
                                        const info = [];
                                        if (dup.inPlan > 0) info.push(t("wbi_in_plan_qty").replace("{qty}", formatNumber(dup.inPlan)));
                                        if (dup.issued.length) info.push(t("wbi_issued_in").replace("{docs}", dup.issued.join(", ")));
                                        return (
                                            <tr key={`${it.part_uid}-${ix}`} className={E["w" + ix] || E["b" + ix] || E["q" + ix] || E["a" + ix] ? "table-danger" : ""}>
                                                <td className="text-center">{ix + 1}</td>
                                                <td>
                                                    <div className="fw-bold text-13">{it.part_name}</div>
                                                    <div className="text-12 text-secondary">{it.part_number} · {it.uom_name}</div>
                                                    {dup.any && (
                                                        <div className={`mt-1 px-2 py-1 rounded border text-12 ${E["a" + ix] ? "border-danger text-danger" : "border-warning bg-warning-subtle"}`}>
                                                            <label className="d-flex align-items-center gap-2 mb-0" style={{ cursor: "pointer" }}>
                                                                <input type="checkbox" checked={it.ack} onChange={(e) => this.setItem(ix, { ack: e.target.checked })} />
                                                                {info.join(" · ")} — {t("wbi_ack_dup")}
                                                            </label>
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    {it.info ? (
                                                        <select className={`form-select form-select-sm ${E["w" + ix] ? "is-invalid" : ""}`} value={it.wbs_element} onChange={(e) => this.onChangeWbs(ix, e.target.value)}>
                                                            {(it.info.wbs || []).map((w) => (
                                                                <option key={w.wbs_element} value={w.wbs_element} disabled={Number(w.available_qty) <= 0 && it.wbs_element !== w.wbs_element}>
                                                                    {w.wbs_element} ({formatNumber(w.available_qty)}){this.isWoWbs(w.wbs_element) ? " ★WO" : ""}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <span className="text-13">{it.wbs_element}</span>
                                                    )}
                                                    {E["w" + ix] ? <div className="text-danger text-12 mt-1">{E["w" + ix]}</div> : it.info && !this.isWoWbs(it.wbs_element) ? <div className="text-12 mt-1" style={{ color: "#b45309" }}>{t("wbi_wbs_differs")}</div> : null}
                                                </td>
                                                <td>
                                                    {it.info ? (
                                                        <select className={`form-select form-select-sm ${E["b" + ix] ? "is-invalid" : ""}`} value={it.stock_lot_uid} onChange={(e) => this.setItem(ix, { stock_lot_uid: e.target.value })}>
                                                            <option value="">{t("wbi_select_lot")}</option>
                                                            {lots.map((l) => (
                                                                <option key={l.stock_lot_uid} value={l.stock_lot_uid} disabled={Number(l.available_qty) <= 0}>
                                                                    {l.plant || ""} / {l.batch_number || "-"} ({formatNumber(l.available_qty)})
                                                                </option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <span className="text-secondary text-12">{t("loading")}</span>
                                                    )}
                                                    {E["b" + ix] && <div className="text-danger text-12 mt-1">{E["b" + ix]}</div>}
                                                </td>
                                                <td className="text-end">{lot ? formatNumber(lot.available_qty) : "-"}</td>
                                                <td className="text-end">
                                                    <input
                                                        type="number"
                                                        min={1}
                                                        step={1}
                                                        className={`form-control form-control-sm text-end ${E["q" + ix] ? "is-invalid" : ""}`}
                                                        value={it.quantity}
                                                        onChange={(e) => this.setItem(ix, { quantity: e.target.value === "" ? "" : Number(e.target.value) })}
                                                    />
                                                    {E["q" + ix] && <div className="text-danger text-12 mt-1">{E["q" + ix]}</div>}
                                                </td>
                                                <td className="text-center">
                                                    <button type="button" className="btn btn-outline-danger btn-sm" title={t("delete")} onClick={() => this.removeItem(ix)}>
                                                        <span className="icon">{""}</span>
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                    {err("items")}

                    {ed.banner && (
                        <div className={`alert ${ed.banner.kind === "error" ? "alert-danger" : "alert-warning"} mt-3 mb-0 py-2 text-13`}>
                            {ed.banner.text}
                            {ed.banner.items?.length > 0 && (
                                <ul className="mb-0 mt-1">
                                    {ed.banner.items.map((c, i) => (
                                        <li key={i}>
                                            {c.part_number}: {t("wbi_requested")} {formatNumber(c.requested)} / {t("wbi_available")} {formatNumber(c.available)}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                    <div className="text-end fw-bold mt-2">
                        {t("wbi_total")} {formatNumber(ed.items.length)} {t("wbi_items_unit")} · {formatNumber(total)} {t("wbi_qty_unit")}
                    </div>
                </Modal.Body>
                <Modal.Footer className="bg-white">
                    <div className="text-12 text-secondary me-auto">{t("wbi_send_locked_hint")}</div>
                    <button type="button" className="btn btn-outline-secondary" onClick={this.tryCloseEditor}>{t("cancel")}</button>
                    <button type="button" className="btn btn-outline-primary" disabled={ed.saving} onClick={this.saveDraft}>{t("wbi_save_draft")}</button>
                    <button type="button" className="btn btn-primary" disabled={ed.saving} onClick={this.askSend}>{t("wbi_save_and_send")}</button>
                </Modal.Footer>
            </Modal>
        );
    }

    renderView() {
        const d = this.state.viewing;
        if (!d) return null;
        const k = (label, value, cls = "col-md-4") => (
            <div className={cls}>
                <div className="text-12 text-secondary">{label}</div>
                <div className="fw-bold">{value || "-"}</div>
            </div>
        );
        return (
            <Modal show size="lg" onHide={() => this.setState({ viewing: null })} centered scrollable>
                <Modal.Header className="bg-white">
                    <Modal.Title className="fs-5">
                        {d.doc_no || t("wbi_no_number_yet")} <span className="ms-2"><StatusChip doc={d} /></span>
                        <div className="text-12 text-secondary fw-normal">{t("wbi_view_sub")} · {this.state.ctx?.wo?.wo_number || ""}</div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="row g-3">
                        {k("Material Document", d.mat_doc ? `${d.mat_doc}${d.mat_year ? " / " + d.mat_year : ""} · ${d.movement_type || ""}` : "")}
                        {k(t("wbi_route"), `Project stock → VAN ${d.van?.code || this.vanLabel()}`)}
                        {k(t("wbi_sent_van_at"), `${String(d.sent_at || "").slice(0, 16).replace("T", " ") || "-"}${d.van_at ? " / " + String(d.van_at).slice(0, 16).replace("T", " ") : ""}`)}
                        {k(t("wbi_need_date"), fmtD(d.need_date))}
                        {k(t("wbi_reason"), this.reasonLabel(d.reason))}
                        {k(t("wbi_requester"), d.requester?.name)}
                        {d.status === "fail" && d.sap_error && (
                            <div className="col-12">
                                <div className="alert alert-danger py-2 mb-0 text-13">⚠ SAP: {d.sap_error}</div>
                            </div>
                        )}
                        {k(t("wbi_remark"), d.remark, "col-12")}
                    </div>
                    <div className="border rounded-3 mt-3 overflow-auto">
                        <table className="table table-sm mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th className="text-center">#</th>
                                    <th>{t("wbi_part")}</th>
                                    <th>WBS</th>
                                    <th>{t("wbi_lot")}</th>
                                    <th className="text-end">{t("wbi_qty_issue")}</th>
                                    <th className="text-end">{t("wbi_used_qty")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(d.items || []).map((i, ix) => (
                                    <tr key={i.uid || ix}>
                                        <td className="text-center">{ix + 1}</td>
                                        <td>
                                            <div className="fw-bold text-13">{i.part_name}</div>
                                            <div className="text-12 text-secondary">{i.part_number}</div>
                                        </td>
                                        <td>{i.wbs_element}</td>
                                        <td>{i.plant || ""} / {i.batch_number || "-"}</td>
                                        <td className="text-end">{formatNumber(i.qty ?? i.quantity)} {i.uom_name}</td>
                                        <td className="text-end">{d.status === "ok" ? formatNumber(i.used_qty ?? 0) : "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Modal.Body>
                <Modal.Footer className="bg-white">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => this.setState({ viewing: null })}>{t("close")}</button>
                    {d.status === "ok" && <button type="button" className="btn btn-primary" onClick={() => this.printDoc(d)}>{t("print")}</button>}
                </Modal.Footer>
            </Modal>
        );
    }

    // E12: ส่งซ้ำไม่ได้เพราะสต็อกไม่พอ -> ให้ยกเลิกแล้วสร้างใหม่
    renderShort() {
        const s = this.state.shortInfo;
        if (!s) return null;
        const done = () => this.setState({ shortInfo: null });
        return (
            <Modal show size="sm" onHide={done} centered style={{ zIndex: 1100 }}>
                <Modal.Header className="bg-white">
                    <Modal.Title className="fs-6">{t("wbi_retry_short_title")}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-14">
                    <div>{s.message || t("wbi_retry_short_body")}</div>
                    {s.items.length > 0 && (
                        <ul className="mb-2 mt-2">
                            {s.items.map((c, i) => (
                                <li key={i}>
                                    {c.part_number}: {t("wbi_requested")} {formatNumber(c.requested)} / {t("wbi_available")} {formatNumber(c.available)}
                                </li>
                            ))}
                        </ul>
                    )}
                    <div className="text-12 text-secondary">{t("wbi_retry_short_hint")}</div>
                </Modal.Body>
                <Modal.Footer className="bg-white">
                    <button type="button" className="btn btn-primary" onClick={done}>{t("wbi_ack")}</button>
                </Modal.Footer>
            </Modal>
        );
    }

    renderConfirm() {
        const c = this.state.confirm;
        if (!c) return null;
        const done = () => this.setState({ confirm: null });
        return (
            <Modal show size="sm" onHide={done} centered style={{ zIndex: 1100 }}>
                <Modal.Header className="bg-white">
                    <Modal.Title className="fs-6">{c.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-14" style={{ whiteSpace: "pre-line" }}>{c.body}</Modal.Body>
                <Modal.Footer className="bg-white">
                    <button type="button" className="btn btn-outline-secondary" onClick={done}>{c.cancel || t("cancel")}</button>
                    <button
                        type="button"
                        className={`btn ${c.danger ? "btn-danger" : "btn-primary"}`}
                        onClick={() => {
                            done();
                            c.onOk && c.onOk();
                        }}
                    >
                        {c.ok || t("confirm")}
                    </button>
                </Modal.Footer>
            </Modal>
        );
    }

    render() {
        const { ctx, ready } = this.state;
        if (!ready || !ctx || ctx.wo?.is_wbs_wo === false || ctx.block_code === "NOT_WBS_WO" || !can("wo_wbs_issue.read")) return null;
        return (
            <>
                {this.renderCard()}
                {this.renderEditor()}
                {this.renderView()}
                {this.renderShort()}
                {this.renderConfirm()}
            </>
        );
    }
}

/**
 * กล่องแยกตาม Material Document ในแท็บ "อะไหล่" ของ Visit — ผูกกับ WO (เห็นเหมือนกันทุก Visit)
 * กล่อง initial (เบิกตั้งต้น) แสดงโดยตารางเดิมของแท็บอยู่แล้ว จึงแสดงเฉพาะกล่อง extra (เบิกเพิ่มเติม) ที่ status ok/send
 * ช่อง "ใช้จริง" แก้ได้เมื่อ status=ok (PUT /:doc_uid/used) ต้องไม่เกินจำนวนเบิก
 */
export class WoWbsPartsBoxes extends Component {
    state = { boxes: [], used: {}, saving: {} };
    mounted = false;

    componentDidMount() {
        this.mounted = true;
        this.load();
        window.addEventListener(EVT_CHANGED, this.load);
    }

    componentDidUpdate(prev) {
        if (prev.woUid !== this.props.woUid) this.load();
    }

    componentWillUnmount() {
        this.mounted = false;
        window.removeEventListener(EVT_CHANGED, this.load);
    }

    load = async () => {
        const { woUid } = this.props;
        if (!woUid || !can("wo_wbs_issue.read")) return;
        const r = await GET(`${BASE}/wo/${woUid}/parts`, null, true, { skipAccessDeniedAlert: true, silent: true });
        if (!this.mounted) return;
        const boxes = r?.status && Array.isArray(r.data?.boxes) ? r.data.boxes.filter((b) => b.type === "extra" && (b.status === "ok" || b.status === "send")) : [];
        const used = {};
        boxes.forEach((b) => (b.items || []).forEach((i) => (used[i.item_uid] = i.used_qty ?? 0)));
        this.setState({ boxes, used, saving: {} });
    };

    isDirty = (box) => (box.items || []).some((i) => String(this.state.used[i.item_uid] ?? "") !== String(i.used_qty ?? 0));
    hasError = (box) => (box.items || []).some((i) => Number(this.state.used[i.item_uid]) > Number(i.qty) || Number(this.state.used[i.item_uid]) < 0 || this.state.used[i.item_uid] === "");

    saveUsed = async (box) => {
        if (this.hasError(box)) return;
        this.setState((s) => ({ saving: { ...s.saving, [box.doc_uid]: true } }));
        loading(true);
        const r = await PUT(`${BASE}/${box.doc_uid}/used`, { items: (box.items || []).map((i) => ({ item_uid: i.item_uid, used_qty: Number(this.state.used[i.item_uid] || 0) })) });
        loading(false);
        if (r?.status) {
            alert("", "success", t("success"), t("wbi_used_saved"));
            await this.load();
            broadcast();
        } else if (this.mounted) this.setState((s) => ({ saving: { ...s.saving, [box.doc_uid]: false } }));
    };

    render() {
        const { boxes, used, saving } = this.state;
        if (!boxes.length) return null;
        const canEnter = can("wo_wbs_issue.update");
        return (
            <div className="mt-3">
                <div className="text-12 text-secondary mb-1">{t("wbi_boxes_note")}</div>
                {boxes.map((b) => {
                    const pend = b.status === "send";
                    const dirty = this.isDirty(b);
                    return (
                        <div key={b.doc_uid} className="border rounded-3 mb-3 overflow-hidden bg-white" style={pend ? { opacity: 0.85 } : undefined}>
                            <div className="d-flex align-items-start gap-2 px-3 py-2 border-bottom" style={{ background: pend ? "#fffbeb" : "#f9fafb" }}>
                                <div>
                                    <div className="text-12 text-secondary">
                                        <span className="icon me-1">{""}</span>
                                        {t("sap_material_document_label")} · <b className="text-primary">{b.doc_no}</b> · {t("wbi_extra_issue")}
                                    </div>
                                    <span className="badge rounded-pill bg-white text-dark border mt-1 fw-bold">
                                        {pend ? t("wbi_waiting_mat_doc") : `${b.mat_doc} / ${b.mat_year}`} <small className="fw-normal text-secondary ms-1">{b.movement_type || ""}</small>
                                    </span>
                                </div>
                                <div className="ms-auto text-end text-12 text-secondary">
                                    {pend ? (
                                        <>
                                            <span className="badge bg-warning-subtle text-warning-emphasis border border-warning">
                                                <span className="spinner-border spinner-border-sm me-1" style={{ width: 10, height: 10, borderWidth: 2 }} />
                                                {t("wbi_waiting_sap")}
                                            </span>
                                            <br />
                                            {t("wbi_sent_at")} {String(b.sent_at || "").slice(0, 16).replace("T", " ")}
                                        </>
                                    ) : (
                                        <>
                                            <span className="badge bg-success-subtle text-success border border-success">✓ {t("wbi_in_van")}</span>
                                            <br />
                                            {t("wbi_in_van")}: {String(b.van_at || "").slice(0, 16).replace("T", " ")}
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="overflow-auto">
                                <table className="table table-sm mb-0 align-middle text-13">
                                    <thead>
                                        <tr>
                                            <th>{t("wbi_part")}</th>
                                            <th className="text-center">{t("wbi_qty_issue_short")}</th>
                                            <th className="text-center">{t("wbi_used_qty")}</th>
                                            <th>{t("wbi_lot")}</th>
                                            <th>WBS</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(b.items || []).map((i) => {
                                            const v = used[i.item_uid];
                                            const bad = Number(v) > Number(i.qty) || Number(v) < 0 || v === "";
                                            return (
                                                <tr key={i.item_uid}>
                                                    <td>
                                                        <div className="fw-bold">{i.part_name}</div>
                                                        <div className="text-12 text-secondary">{i.part_number}</div>
                                                        {i.over_issued_used && <div className="text-12" style={{ color: "#b45309" }}>{t("wbi_over_used")}</div>}
                                                        {!pend && <div className="text-12 text-success">✓ {b.mat_doc} · {b.movement_type}</div>}
                                                    </td>
                                                    <td className="text-center">{formatNumber(i.qty)}</td>
                                                    <td className="text-center" style={{ width: 110 }}>
                                                        <input
                                                            type="number"
                                                            min={0}
                                                            max={i.qty}
                                                            className={`form-control form-control-sm text-center ${bad ? "is-invalid" : ""}`}
                                                            value={v ?? ""}
                                                            disabled={pend || !canEnter}
                                                            onChange={(e) => this.setState((s) => ({ used: { ...s.used, [i.item_uid]: e.target.value === "" ? "" : Number(e.target.value) } }))}
                                                        />
                                                        {bad && <div className="text-danger text-12">{t("wbi_err_used_over")}</div>}
                                                    </td>
                                                    <td>
                                                        <span className="badge bg-light text-dark border fw-normal">{i.plant || ""} / {i.batch_number || "-"}</span>
                                                    </td>
                                                    <td>
                                                        <span className="badge bg-light text-dark border fw-normal" title={i.wbs_element}>{i.wbs_element}</span>
                                                    </td>
                                                    <td>
                                                        <span className="icon text-secondary" title={t("wbi_locked")}>{""}</span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            {!pend && canEnter && (
                                <div className="d-flex justify-content-end px-3 py-2 border-top">
                                    <button type="button" className="btn btn-primary btn-sm" disabled={!dirty || this.hasError(b) || saving[b.doc_uid]} onClick={() => this.saveUsed(b)}>
                                        {t("wbi_save_used")}
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }
}
